// src/cockpit4/components/FinancialStatements.tsx
// ============================================
// 完整财务三表（财务中心 P0-功能1）- v2.0 详细改进计划
//
// 功能：
//   1. 三表 Tab 切换：利润表 / 资产负债表 / 现金流量表
//   2. 利润表：收入 → 成本 → 费用 → 利润（标准格式）
//   3. 资产负债表：资产 = 负债 + 所有者权益（含平衡校验）
//   4. 现金流量表：经营 / 投资 / 筹资 三大活动
//   5. 关键财务指标自动计算（≥10 个）：毛利率、净利率、ROE、资产负债率等
//   6. 同比/环比对比（本月 vs 上月）
//   7. 数据可钻取（点击科目展开/收起明细）
//   8. 人工录入表单（收入自动同步，其他科目手动录入）
//   9. 数据来源标注（DataSourceBadge：绿色=自动统计 / 黄色=部分统计 / 灰色=待接入）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 收入：RuntimeManager.query('revenue_engine' as any, { type: 'today_revenue' })
//           自动同步 RevenueEngine 聚合的本月累计收入（monthAmount）
//   - 成本/费用/资产/负债/现金流：localStorage（key: xeaos_finance_statements）
//           持久化用户人工录入数据
//   - 无数据时显示"暂无数据 / 待录入"空状态，绝不展示假数字
//
// 权限控制：
//   - 仅超级管理员可访问财务中心
//   - 非超管显示红色 Lock 图标 + "权限受限"提示
//
// 白皮书规格：v2.0 详细改进计划 - 财务中心 P0-功能1
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Scale,
  Wallet,
  TrendingUp,
  TrendingDown,
  Lock,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  X,
  Save,
  Calculator,
  PieChart,
  DollarSign,
  Percent,
  Database,
  Edit3,
  BarChart3,
  Activity,
  CircleDot,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { useUserStore } from "../../stores/userStore";
// Phase 5 财务 Vault 化：三表写透 Vault（跨设备唯一真相源）+ 挂载回填
import { writeFinanceJson, hydrateFinanceKey } from "../../services/financeVaultStorage";

// ============================================
// 类型定义
// ============================================

/** 财务三表数据模型 */
interface StatementData {
  // 利润表
  revenue: number;            // 收入（自动同步自 RevenueEngine）
  costOfGoodsSold: number;    // 营业成本（手动）
  operatingExpenses: number;  // 运营费用（手动）
  marketingExpenses: number;  // 营销费用（手动）
  adminExpenses: number;      // 管理费用（手动）
  taxExpenses: number;        // 税费（手动）
  // 资产负债表
  currentAssets: number;      // 流动资产（手动）
  fixedAssets: number;        // 固定资产（手动）
  intangibleAssets: number;   // 无形资产（手动）
  currentLiabilities: number; // 流动负债（手动）
  longTermLiabilities: number;// 长期负债（手动）
  ownersEquity: number;       // 所有者权益（手动）
  // 现金流量表
  operatingCashFlow: number;  // 经营现金流（手动）
  investingCashFlow: number;  // 投资现金流（手动）
  financingCashFlow: number;  // 筹资现金流（手动）
  lastUpdated: string;        // 最后更新时间
  dataSource: "real" | "partial" | "degraded";
}

/** localStorage 持久化结构（含本月 + 上月快照用于同比/环比） */
interface PersistedFinance {
  current: StatementData;
  previousMonth: StatementData | null;
}

/** 数据来源徽章类型 */
type BadgeSource = "auto" | "manual" | "formula" | "empty";

/** Tab 类型 */
type StatementTab = "income" | "balance" | "cashflow";

/** 可钻取的报表行定义 */
interface StatementRowDef {
  key: string;
  label: string;
  value: number;
  /** 数据来源：auto=自动统计 / manual=人工录入 / formula=公式计算 */
  source: "auto" | "manual" | "formula";
  /** 人工字段是否已录入（用于徽章判定） */
  entered: boolean;
  /** 明细说明（钻取展开后展示） */
  description: string;
  /** 子行（钻取展开后展示的明细构成） */
  children?: Array<{ label: string; value: number; source: "auto" | "manual" | "formula" }>;
  /** 是否加粗（合计行） */
  bold?: boolean;
  /** 主题色 */
  color?: string;
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_finance_statements";

/** 金额格式化（人民币） */
const currencyFormatter = new Intl.NumberFormat("zh-CN", {
  style: "currency",
  currency: "CNY",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** 空数据模板（绝不展示假数字，全部为 0） */
function createEmptyStatement(): StatementData {
  return {
    revenue: 0,
    costOfGoodsSold: 0,
    operatingExpenses: 0,
    marketingExpenses: 0,
    adminExpenses: 0,
    taxExpenses: 0,
    currentAssets: 0,
    fixedAssets: 0,
    intangibleAssets: 0,
    currentLiabilities: 0,
    longTermLiabilities: 0,
    ownersEquity: 0,
    operatingCashFlow: 0,
    investingCashFlow: 0,
    financingCashFlow: 0,
    lastUpdated: "",
    dataSource: "degraded",
  };
}

/** 人工录入字段配置（按报表分组） */
const MANUAL_FIELDS: Array<{
  key: keyof StatementData;
  label: string;
  group: "利润表" | "资产负债表" | "现金流量表";
  hint: string;
}> = [
  { key: "costOfGoodsSold", label: "营业成本", group: "利润表", hint: "产品/服务的直接成本" },
  { key: "operatingExpenses", label: "运营费用", group: "利润表", hint: "日常运营开支" },
  { key: "marketingExpenses", label: "营销费用", group: "利润表", hint: "推广/广告投放" },
  { key: "adminExpenses", label: "管理费用", group: "利润表", hint: "行政/办公/人力" },
  { key: "taxExpenses", label: "税费", group: "利润表", hint: "各项税金合计" },
  { key: "currentAssets", label: "流动资产", group: "资产负债表", hint: "现金/应收/存货等" },
  { key: "fixedAssets", label: "固定资产", group: "资产负债表", hint: "设备/房产等" },
  { key: "intangibleAssets", label: "无形资产", group: "资产负债表", hint: "专利/品牌/商誉" },
  { key: "currentLiabilities", label: "流动负债", group: "资产负债表", hint: "短期借款/应付等" },
  { key: "longTermLiabilities", label: "长期负债", group: "资产负债表", hint: "长期借款/债券" },
  { key: "ownersEquity", label: "所有者权益", group: "资产负债表", hint: "实收资本+留存收益" },
  { key: "operatingCashFlow", label: "经营现金流", group: "现金流量表", hint: "经营活动现金净额" },
  { key: "investingCashFlow", label: "投资现金流", group: "现金流量表", hint: "投资活动现金净额" },
  { key: "financingCashFlow", label: "筹资现金流", group: "现金流量表", hint: "筹资活动现金净额" },
];

// ============================================
// 工具函数
// ============================================

/** 格式化金额 */
function formatCurrency(v: number): string {
  if (!isFinite(v)) return "¥ 0.00";
  return currencyFormatter.format(v);
}

/** 格式化百分比 */
function formatPercent(ratio: number): string {
  if (!isFinite(ratio)) return "0.00%";
  return `${(ratio * 100).toFixed(2)}%`;
}

/** 从 localStorage 读取持久化数据 */
function loadFinance(): PersistedFinance | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedFinance;
    if (!parsed?.current) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** 写入 localStorage + 写透 Vault（财务唯一真相源：XEAOS/Finance/statements.json） */
function saveFinance(data: PersistedFinance): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // 容量满或隐私模式，忽略写入错误
  }
  // Phase 5：写透 Vault（失败静默降级到 localStorage，不抛异常）
  void writeFinanceJson("statements.json", STORAGE_KEY, data);
}

/** 同步 RevenueEngine 真实收入数据 */
async function syncRevenue(): Promise<{
  revenue: number;
  source: "real" | "partial" | "degraded";
  todayAmount: number;
  monthAmount: number;
}> {
  try {
    // RuntimeManager 未初始化时直接降级（不展示假数据）
    if (typeof RuntimeManager.isInitialized === "function" && !RuntimeManager.isInitialized()) {
      return { revenue: 0, source: "degraded", todayAmount: 0, monthAmount: 0 };
    }
    const resp = await RuntimeManager.query("revenue_engine" as any, { type: "today_revenue" });
    if (resp?.success && resp.data) {
      const d = resp.data as any;
      const monthAmount = Number(d.monthAmount) || 0;
      const todayAmount = Number(d.todayAmount) || 0;
      const ds = (d.dataSource as "real" | "partial" | "degraded") || "real";
      return { revenue: monthAmount, source: ds, todayAmount, monthAmount };
    }
    return { revenue: 0, source: "degraded", todayAmount: 0, monthAmount: 0 };
  } catch {
    return { revenue: 0, source: "degraded", todayAmount: 0, monthAmount: 0 };
  }
}

/** 计算全部关键财务指标 */
function computeIndicators(d: StatementData) {
  const revenue = d.revenue || 0;
  const cogs = d.costOfGoodsSold || 0;
  const opEx = d.operatingExpenses || 0;
  const mktEx = d.marketingExpenses || 0;
  const admEx = d.adminExpenses || 0;
  const tax = d.taxExpenses || 0;

  // 利润表派生
  const grossProfit = revenue - cogs;                                  // 毛利润
  const grossMargin = revenue > 0 ? grossProfit / revenue : 0;         // 毛利率
  const operatingProfit = grossProfit - opEx - mktEx - admEx;          // 营业利润
  const operatingMargin = revenue > 0 ? operatingProfit / revenue : 0; // 营业利润率
  const netProfit = operatingProfit - tax;                             // 净利润
  const netMargin = revenue > 0 ? netProfit / revenue : 0;             // 净利率
  const expenseRatio = revenue > 0 ? (opEx + mktEx + admEx) / revenue : 0; // 期间费用率

  // 资产负债表派生
  const totalAssets = (d.currentAssets || 0) + (d.fixedAssets || 0) + (d.intangibleAssets || 0);
  const totalLiabilities = (d.currentLiabilities || 0) + (d.longTermLiabilities || 0);
  const debtRatio = totalAssets > 0 ? totalLiabilities / totalAssets : 0;        // 资产负债率
  const roe = d.ownersEquity > 0 ? netProfit / d.ownersEquity : 0;               // ROE
  const currentRatio = d.currentLiabilities > 0 ? d.currentAssets / d.currentLiabilities : 0; // 流动比率
  const equityMultiplier = d.ownersEquity > 0 ? totalAssets / d.ownersEquity : 0; // 权益乘数
  // 资产负债表平衡校验：资产 - 负债 - 所有者权益，理想为 0
  const balanceCheck = totalAssets - totalLiabilities - (d.ownersEquity || 0);

  // 现金流量表派生
  const cashNetIncrease =
    (d.operatingCashFlow || 0) + (d.investingCashFlow || 0) + (d.financingCashFlow || 0);

  return {
    revenue,
    cogs,
    opEx,
    mktEx,
    admEx,
    tax,
    grossProfit,
    grossMargin,
    operatingProfit,
    operatingMargin,
    netProfit,
    netMargin,
    expenseRatio,
    totalAssets,
    totalLiabilities,
    debtRatio,
    roe,
    currentRatio,
    equityMultiplier,
    balanceCheck,
    cashNetIncrease,
  };
}

/** 计算同比/环比 delta（本月 vs 上月），返回百分比变化 */
function computeDelta(current: number, previous: number | undefined): number | undefined {
  if (previous === undefined || previous === 0) return undefined;
  return ((current - previous) / Math.abs(previous)) * 100;
}

// ============================================
// 子组件：数据来源徽章
// ============================================

interface DataSourceBadgeProps {
  source: BadgeSource;
}

function DataSourceBadge({ source }: DataSourceBadgeProps) {
  // 绿色=自动统计 / 黄色=部分统计（人工录入）/ 灰色=待接入
  const config = {
    auto: { label: "自动统计", color: "#10B981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)" },
    formula: { label: "自动统计", color: "#10B981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)" },
    manual: { label: "部分统计", color: "#F59E0B", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.3)" },
    empty: { label: "待接入", color: "#6B7280", bg: "rgba(107,114,128,0.12)", border: "rgba(107,114,128,0.3)" },
  } as const;
  const c = config[source];
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase tracking-wider whitespace-nowrap"
      style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}` }}
    >
      <CircleDot size={7} />
      {c.label}
    </span>
  );
}

// ============================================
// 子组件：可钻取报表行
// ============================================

interface StatementRowProps {
  row: StatementRowDef;
  /** 行级动画延迟 */
  delay?: number;
}

function StatementRow({ row, delay = 0 }: StatementRowProps) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = (row.children?.length ?? 0) > 0;
  const badgeSource: BadgeSource =
    row.source === "auto"
      ? "auto"
      : row.source === "formula"
      ? "formula"
      : row.entered
      ? "manual"
      : "empty";

  return (
    <div className="border-b border-white/[0.04] last:border-b-0">
      <motion.div
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.25, delay }}
        onClick={() => hasChildren && setExpanded((v) => !v)}
        className={`flex items-center justify-between px-3 py-2 transition-colors ${
          hasChildren ? "cursor-pointer hover:bg-white/[0.03]" : ""
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* 展开/收起箭头（仅有子行时显示） */}
          <span className="w-3 flex-shrink-0">
            {hasChildren &&
              (expanded ? (
                <ChevronDown size={11} className="text-gray-400" />
              ) : (
                <ChevronRight size={11} className="text-gray-400" />
              ))}
          </span>
          <span
            className={`text-[11px] font-mono truncate ${
              row.bold ? "text-white font-semibold" : "text-gray-300"
            }`}
            style={row.color ? { color: row.color } : undefined}
          >
            {row.label}
          </span>
          <DataSourceBadge source={badgeSource} />
        </div>
        <span
          className={`text-[12px] font-mono tabular-nums ${
            row.bold ? "text-white font-bold" : row.value < 0 ? "text-red-400" : "text-gray-200"
          }`}
        >
          {formatCurrency(row.value)}
        </span>
      </motion.div>

      {/* 钻取明细（展开后） */}
      <AnimatePresence initial={false}>
        {expanded && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden bg-white/[0.015]"
          >
            <div className="px-3 py-2 pl-10 space-y-1.5">
              <p className="text-[9px] font-mono text-gray-500 leading-relaxed">
                {row.description}
              </p>
              <div className="space-y-1 pt-1">
                {row.children!.map((child) => {
                  const childBadge: BadgeSource =
                    child.source === "auto"
                      ? "auto"
                      : child.source === "formula"
                      ? "formula"
                      : child.value > 0
                      ? "manual"
                      : "empty";
                  return (
                    <div
                      key={child.label}
                      className="flex items-center justify-between text-[10px] font-mono"
                    >
                      <div className="flex items-center gap-1.5">
                        <CircleDot size={6} className="text-gray-600" />
                        <span className="text-gray-400">{child.label}</span>
                        <DataSourceBadge source={childBadge} />
                      </div>
                      <span
                        className={`tabular-nums ${
                          child.value < 0 ? "text-red-400" : "text-gray-300"
                        }`}
                      >
                        {formatCurrency(child.value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：财务指标卡片
// ============================================

interface MetricCardProps {
  label: string;
  value: string;
  icon: typeof TrendingUp;
  color: string;
  /** 同比/环比百分比变化 */
  deltaPercent?: number;
  deltaLabel?: string;
  delay?: number;
}

function MetricCard({
  label,
  value,
  icon: Icon,
  color,
  deltaPercent,
  deltaLabel = "环比",
  delay = 0,
}: MetricCardProps) {
  const deltaStatus: "up" | "down" | "flat" | "none" =
    deltaPercent === undefined
      ? "none"
      : deltaPercent > 0
      ? "up"
      : deltaPercent < 0
      ? "down"
      : "flat";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 overflow-hidden"
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }}
      />
      <div className="flex items-center justify-between mb-1.5">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={12} style={{ color }} />
        </div>
        {deltaStatus !== "none" && (
          <span
            className={`flex items-center gap-0.5 text-[9px] font-mono ${
              deltaStatus === "up"
                ? "text-emerald-400"
                : deltaStatus === "down"
                ? "text-red-400"
                : "text-gray-500"
            }`}
            title={deltaLabel}
          >
            {deltaStatus === "up" ? (
              <TrendingUp size={9} />
            ) : deltaStatus === "down" ? (
              <TrendingDown size={9} />
            ) : null}
            {deltaPercent !== undefined ? `${deltaPercent.toFixed(1)}%` : ""}
            <span className="text-[7px] text-gray-600 ml-0.5">{deltaLabel}</span>
          </span>
        )}
      </div>
      <div className="text-[16px] font-mono font-bold text-white tracking-tight leading-none">
        {value}
      </div>
      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-1">
        {label}
      </div>
    </motion.div>
  );
}

// ============================================
// 主组件
// ============================================

export default function FinancialStatements() {
  // 权限检查：仅超级管理员可访问财务中心
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());

  const [activeTab, setActiveTab] = useState<StatementTab>("income");
  const [data, setData] = useState<StatementData>(() => loadFinance()?.current ?? createEmptyStatement());
  const [previousMonth, setPreviousMonth] = useState<StatementData | null>(
    () => loadFinance()?.previousMonth ?? null
  );
  const [revenueSource, setRevenueSource] = useState<"real" | "partial" | "degraded">("degraded");
  const [todayAmount, setTodayAmount] = useState<number>(0);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [showEntryForm, setShowEntryForm] = useState<boolean>(false);

  // 同步收入（自动从 RevenueEngine 拉取，绝不假数据）
  const refreshRevenue = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await syncRevenue();
      setRevenueSource(result.source);
      setTodayAmount(result.todayAmount);
      setData((prev) => {
        // 仅当引擎返回真实/部分数据且金额>0 时覆盖收入字段
        const newRevenue =
          result.source !== "degraded" && result.revenue > 0 ? result.revenue : prev.revenue;
        const next: StatementData = {
          ...prev,
          revenue: newRevenue,
          dataSource: result.source,
          lastUpdated: newRevenue !== prev.revenue ? new Date().toISOString() : prev.lastUpdated,
        };
        // 同步回 localStorage（保留 previousMonth）
        const existing = loadFinance();
        saveFinance({ current: next, previousMonth: existing?.previousMonth ?? null });
        return next;
      });
    } finally {
      setSyncing(false);
    }
  }, []);

  // 初始化：首次加载同步收入
  useEffect(() => {
    void refreshRevenue();
  }, [refreshRevenue]);

  // Phase 5：挂载时从 Vault 回填三表数据（Vault 为唯一真相源，localStorage 仅缓存）
  useEffect(() => {
    async function hydrateFromVault() {
      try {
        const hydrated = await hydrateFinanceKey<PersistedFinance>("statements.json", STORAGE_KEY);
        if (hydrated?.current) {
          setData(hydrated.current);
          setPreviousMonth(hydrated.previousMonth ?? null);
        }
      } catch {
        /* Vault 不可达时保持本地数据 */
      }
    }
    void hydrateFromVault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 计算指标
  const indicators = useMemo(() => computeIndicators(data), [data]);

  // 上月指标（用于同比/环比对比）
  const prevIndicators = useMemo(
    () => (previousMonth ? computeIndicators(previousMonth) : null),
    [previousMonth]
  );

  // 是否存在任何数据（无数据时显示空状态）
  const hasAnyData = useMemo(() => {
    const manualEntered = MANUAL_FIELDS.some((f) => (data[f.key] as number) > 0);
    const revenueAvailable = data.revenue > 0 && revenueSource !== "degraded";
    return manualEntered || revenueAvailable;
  }, [data, revenueSource]);

  // 收入是否已真实接入
  const revenueEntered = data.revenue > 0 && revenueSource !== "degraded";

  // ============================================
  // 利润表行定义
  // ============================================
  const incomeRows: StatementRowDef[] = useMemo(
    () => [
      {
        key: "revenue",
        label: "营业收入",
        value: indicators.revenue,
        source: "auto",
        entered: revenueEntered,
        description: "由 RevenueEngine 自动聚合 CRM 成交 + SKU 销售 + 产品 Runtime 收入，按本月累计口径同步。",
        children: [
          { label: "本月累计（monthAmount）", value: indicators.revenue, source: "auto" },
          { label: "今日收入（todayAmount）", value: todayAmount, source: "auto" },
        ],
        bold: true,
        color: "#10B981",
      },
      {
        key: "cogs",
        label: "营业成本",
        value: -indicators.cogs,
        source: "manual",
        entered: data.costOfGoodsSold > 0,
        description: "产品/服务的直接成本（人工录入）。毛利润 = 营业收入 - 营业成本。",
        children: [{ label: "营业成本（录入值）", value: -data.costOfGoodsSold, source: "manual" }],
      },
      {
        key: "grossProfit",
        label: "毛利润",
        value: indicators.grossProfit,
        source: "formula",
        entered: revenueEntered || data.costOfGoodsSold > 0,
        description: "毛利润 = 营业收入 - 营业成本；毛利率 = 毛利润 / 营业收入。",
        children: [
          { label: "营业收入", value: indicators.revenue, source: "auto" },
          { label: "营业成本", value: -indicators.cogs, source: "manual" },
          { label: "毛利率", value: indicators.grossMargin, source: "formula" },
        ],
        bold: true,
        color: "#06B6D4",
      },
      {
        key: "opEx",
        label: "运营费用",
        value: -indicators.opEx,
        source: "manual",
        entered: data.operatingExpenses > 0,
        description: "日常运营开支（人工录入）。",
        children: [{ label: "运营费用（录入值）", value: -data.operatingExpenses, source: "manual" }],
      },
      {
        key: "mktEx",
        label: "营销费用",
        value: -indicators.mktEx,
        source: "manual",
        entered: data.marketingExpenses > 0,
        description: "推广/广告投放支出（人工录入）。",
        children: [{ label: "营销费用（录入值）", value: -data.marketingExpenses, source: "manual" }],
      },
      {
        key: "admEx",
        label: "管理费用",
        value: -indicators.admEx,
        source: "manual",
        entered: data.adminExpenses > 0,
        description: "行政/办公/人力等管理开支（人工录入）。",
        children: [{ label: "管理费用（录入值）", value: -data.adminExpenses, source: "manual" }],
      },
      {
        key: "operatingProfit",
        label: "营业利润",
        value: indicators.operatingProfit,
        source: "formula",
        entered: revenueEntered,
        description: "营业利润 = 毛利润 - 运营费用 - 营销费用 - 管理费用。",
        children: [
          { label: "毛利润", value: indicators.grossProfit, source: "formula" },
          { label: "期间费用合计", value: -(indicators.opEx + indicators.mktEx + indicators.admEx), source: "formula" },
          { label: "营业利润率", value: indicators.operatingMargin, source: "formula" },
        ],
        bold: true,
        color: "#8B5CF6",
      },
      {
        key: "tax",
        label: "所得税费用",
        value: -indicators.tax,
        source: "manual",
        entered: data.taxExpenses > 0,
        description: "各项税金合计（人工录入）。",
        children: [{ label: "税费（录入值）", value: -data.taxExpenses, source: "manual" }],
      },
      {
        key: "netProfit",
        label: "净利润",
        value: indicators.netProfit,
        source: "formula",
        entered: revenueEntered,
        description: "净利润 = 营业利润 - 所得税费用；净利率 = 净利润 / 营业收入。",
        children: [
          { label: "营业利润", value: indicators.operatingProfit, source: "formula" },
          { label: "所得税费用", value: -indicators.tax, source: "manual" },
          { label: "净利率", value: indicators.netMargin, source: "formula" },
        ],
        bold: true,
        color: "#10B981",
      },
    ],
    [indicators, data, revenueEntered, todayAmount]
  );

  // ============================================
  // 资产负债表行定义
  // ============================================
  const balanceRows: StatementRowDef[] = useMemo(
    () => [
      {
        key: "currentAssets",
        label: "流动资产",
        value: data.currentAssets,
        source: "manual",
        entered: data.currentAssets > 0,
        description: "现金、应收账款、存货等可在一年内变现的资产（人工录入）。",
        children: [{ label: "流动资产（录入值）", value: data.currentAssets, source: "manual" }],
      },
      {
        key: "fixedAssets",
        label: "固定资产",
        value: data.fixedAssets,
        source: "manual",
        entered: data.fixedAssets > 0,
        description: "设备、房产等长期使用资产（人工录入）。",
        children: [{ label: "固定资产（录入值）", value: data.fixedAssets, source: "manual" }],
      },
      {
        key: "intangibleAssets",
        label: "无形资产",
        value: data.intangibleAssets,
        source: "manual",
        entered: data.intangibleAssets > 0,
        description: "专利、品牌、商誉等无形资产（人工录入）。",
        children: [{ label: "无形资产（录入值）", value: data.intangibleAssets, source: "manual" }],
      },
      {
        key: "totalAssets",
        label: "资产总计",
        value: indicators.totalAssets,
        source: "formula",
        entered: indicators.totalAssets > 0,
        description: "总资产 = 流动资产 + 固定资产 + 无形资产。",
        children: [
          { label: "流动资产", value: data.currentAssets, source: "manual" },
          { label: "固定资产", value: data.fixedAssets, source: "manual" },
          { label: "无形资产", value: data.intangibleAssets, source: "manual" },
        ],
        bold: true,
        color: "#06B6D4",
      },
      {
        key: "currentLiabilities",
        label: "流动负债",
        value: data.currentLiabilities,
        source: "manual",
        entered: data.currentLiabilities > 0,
        description: "短期借款、应付账款等一年内到期负债（人工录入）。",
        children: [{ label: "流动负债（录入值）", value: data.currentLiabilities, source: "manual" }],
      },
      {
        key: "longTermLiabilities",
        label: "长期负债",
        value: data.longTermLiabilities,
        source: "manual",
        entered: data.longTermLiabilities > 0,
        description: "长期借款、应付债券等长期负债（人工录入）。",
        children: [{ label: "长期负债（录入值）", value: data.longTermLiabilities, source: "manual" }],
      },
      {
        key: "totalLiabilities",
        label: "负债合计",
        value: indicators.totalLiabilities,
        source: "formula",
        entered: indicators.totalLiabilities > 0,
        description: "总负债 = 流动负债 + 长期负债。",
        children: [
          { label: "流动负债", value: data.currentLiabilities, source: "manual" },
          { label: "长期负债", value: data.longTermLiabilities, source: "manual" },
        ],
        bold: true,
        color: "#F59E0B",
      },
      {
        key: "ownersEquity",
        label: "所有者权益",
        value: data.ownersEquity,
        source: "manual",
        entered: data.ownersEquity > 0,
        description: "实收资本 + 资本公积 + 留存收益（人工录入）。",
        children: [{ label: "所有者权益（录入值）", value: data.ownersEquity, source: "manual" }],
      },
      {
        key: "balanceCheck",
        label: "平衡校验（资产-负债-权益）",
        value: indicators.balanceCheck,
        source: "formula",
        entered: indicators.totalAssets > 0 || indicators.totalLiabilities > 0 || data.ownersEquity > 0,
        description: "资产 = 负债 + 所有者权益。理想值为 0，非 0 表示录入不平衡需复核。",
        children: [
          { label: "资产总计", value: indicators.totalAssets, source: "formula" },
          { label: "负债合计", value: -indicators.totalLiabilities, source: "formula" },
          { label: "所有者权益", value: -data.ownersEquity, source: "manual" },
        ],
        bold: true,
        color: Math.abs(indicators.balanceCheck) < 0.01 ? "#10B981" : "#EF4444",
      },
    ],
    [indicators, data]
  );

  // ============================================
  // 现金流量表行定义
  // ============================================
  const cashflowRows: StatementRowDef[] = useMemo(
    () => [
      {
        key: "operatingCashFlow",
        label: "经营活动现金流",
        value: data.operatingCashFlow,
        source: "manual",
        entered: data.operatingCashFlow > 0,
        description: "销售商品/提供劳务收到的现金等经营活动现金净额（人工录入）。",
        children: [{ label: "经营现金流（录入值）", value: data.operatingCashFlow, source: "manual" }],
        color: "#10B981",
      },
      {
        key: "investingCashFlow",
        label: "投资活动现金流",
        value: data.investingCashFlow,
        source: "manual",
        entered: data.investingCashFlow > 0,
        description: "购建/处置长期资产等投资活动现金净额（人工录入）。",
        children: [{ label: "投资现金流（录入值）", value: data.investingCashFlow, source: "manual" }],
        color: "#06B6D4",
      },
      {
        key: "financingCashFlow",
        label: "筹资活动现金流",
        value: data.financingCashFlow,
        source: "manual",
        entered: data.financingCashFlow > 0,
        description: "吸收投资/借款/还款等筹资活动现金净额（人工录入）。",
        children: [{ label: "筹资现金流（录入值）", value: data.financingCashFlow, source: "manual" }],
        color: "#8B5CF6",
      },
      {
        key: "cashNetIncrease",
        label: "现金净增加额",
        value: indicators.cashNetIncrease,
        source: "formula",
        entered:
          data.operatingCashFlow > 0 || data.investingCashFlow > 0 || data.financingCashFlow > 0,
        description: "现金净增加 = 经营现金流 + 投资现金流 + 筹资现金流。",
        children: [
          { label: "经营活动现金流", value: data.operatingCashFlow, source: "manual" },
          { label: "投资活动现金流", value: data.investingCashFlow, source: "manual" },
          { label: "筹资活动现金流", value: data.financingCashFlow, source: "manual" },
        ],
        bold: true,
        color: "#10B981",
      },
    ],
    [indicators, data]
  );

  // 当前 Tab 行
  const activeRows = useMemo(() => {
    if (activeTab === "income") return incomeRows;
    if (activeTab === "balance") return balanceRows;
    return cashflowRows;
  }, [activeTab, incomeRows, balanceRows, cashflowRows]);

  // ============================================
  // 录入表单：保存
  // ============================================
  const handleSaveEntry = useCallback(
    (formData: Partial<StatementData>) => {
      // 保存前：将当前数据快照为 previousMonth（用于同比/环比）
      const newCurrent: StatementData = {
        ...data,
        ...formData,
        // 收入由 RevenueEngine 自动同步，不接收手动录入值
        revenue: data.revenue,
        lastUpdated: new Date().toISOString(),
        dataSource: data.dataSource,
      } as StatementData;

      const persisted: PersistedFinance = {
        current: newCurrent,
        previousMonth: data,
      };
      saveFinance(persisted);
      setData(newCurrent);
      setPreviousMonth(data);
      setShowEntryForm(false);
    },
    [data]
  );

  // ============================================
  // 权限拦截：非超级管理员
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-md p-6">
        <div className="flex flex-col items-center justify-center py-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-3"
          >
            <Lock size={22} className="text-red-400" />
          </motion.div>
          <p className="text-[13px] font-mono text-red-300 mb-1">权限受限</p>
          <p className="text-[10px] font-mono text-gray-500 text-center leading-relaxed">
            财务中心仅对超级管理员开放。
            <br />
            财务三表涉及企业核心数据，普通员工无权访问。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 渲染：空状态
  // ============================================
  if (!hasAnyData) {
    return (
      <div className="space-y-3">
        {/* 顶部操作栏 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-gray-500">
              数据源：RevenueEngine（收入自动）+ localStorage（人工录入）
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => void refreshRevenue()}
              disabled={syncing}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition disabled:opacity-50"
            >
              <RefreshCw size={10} className={syncing ? "animate-spin" : ""} />
              {syncing ? "同步中" : "同步收入"}
            </button>
            <button
              onClick={() => setShowEntryForm(true)}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
            >
              <Edit3 size={10} />
              录入数据
            </button>
          </div>
        </div>

        {/* 空状态 */}
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-10">
          <div className="flex flex-col items-center justify-center py-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center mb-3"
            >
              <Database size={24} className="text-gray-600" />
            </motion.div>
            <p className="text-[12px] font-mono text-gray-400 mb-1">暂无数据 / 待录入</p>
            <p className="text-[10px] font-mono text-gray-600 text-center leading-relaxed max-w-md">
              财务三表尚未录入任何数据。
              <br />
              收入将由 RevenueEngine 自动同步，成本/费用/资产/负债/现金流需人工录入。
              <br />
              点击右上角"录入数据"开始，或"同步收入"拉取真实收入。
            </p>
            <p className="text-[9px] font-mono text-gray-700 mt-3">
              白皮书铁律一：禁止假数据，无数据时显示空状态
            </p>
          </div>
        </div>

        {/* 录入表单 */}
        <AnimatePresence>
          {showEntryForm && (
            <EntryFormModal
              current={data}
              revenueSource={revenueSource}
              onClose={() => setShowEntryForm(false)}
              onSave={handleSaveEntry}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ============================================
  // 渲染：主视图
  // ============================================
  const tabs: Array<{ key: StatementTab; label: string; icon: typeof FileText }> = [
    { key: "income", label: "利润表", icon: FileText },
    { key: "balance", label: "资产负债表", icon: Scale },
    { key: "cashflow", label: "现金流量表", icon: Wallet },
  ];

  return (
    <div className="space-y-3">
      {/* 顶部操作栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-500">
            数据源：RevenueEngine（收入自动）+ localStorage（人工录入）
          </span>
          {data.lastUpdated && (
            <span className="text-[9px] font-mono text-gray-600">
              最后更新：{new Date(data.lastUpdated).toLocaleString("zh-CN")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => void refreshRevenue()}
            disabled={syncing}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition disabled:opacity-50"
          >
            <RefreshCw size={10} className={syncing ? "animate-spin" : ""} />
            {syncing ? "同步中" : "同步收入"}
          </button>
          <button
            onClick={() => setShowEntryForm(true)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
          >
            <Edit3 size={10} />
            录入数据
          </button>
        </div>
      </div>

      {/* 关键财务指标（≥10 个，自动计算 + 同比/环比对比） */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Calculator size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
            关键财务指标（自动计算）
          </span>
          <span className="text-[9px] font-mono text-gray-600">
            本月 vs 上月
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <MetricCard
            label="毛利润"
            value={formatCurrency(indicators.grossProfit)}
            icon={DollarSign}
            color="#10B981"
            deltaPercent={computeDelta(indicators.grossProfit, prevIndicators?.grossProfit)}
            delay={0}
          />
          <MetricCard
            label="毛利率"
            value={formatPercent(indicators.grossMargin)}
            icon={Percent}
            color="#10B981"
            deltaPercent={computeDelta(indicators.grossMargin, prevIndicators?.grossMargin)}
            delay={0.03}
          />
          <MetricCard
            label="营业利润"
            value={formatCurrency(indicators.operatingProfit)}
            icon={Activity}
            color="#8B5CF6"
            deltaPercent={computeDelta(indicators.operatingProfit, prevIndicators?.operatingProfit)}
            delay={0.06}
          />
          <MetricCard
            label="营业利润率"
            value={formatPercent(indicators.operatingMargin)}
            icon={Percent}
            color="#8B5CF6"
            deltaPercent={computeDelta(indicators.operatingMargin, prevIndicators?.operatingMargin)}
            delay={0.09}
          />
          <MetricCard
            label="净利润"
            value={formatCurrency(indicators.netProfit)}
            icon={DollarSign}
            color="#06B6D4"
            deltaPercent={computeDelta(indicators.netProfit, prevIndicators?.netProfit)}
            delay={0.12}
          />
          <MetricCard
            label="净利率"
            value={formatPercent(indicators.netMargin)}
            icon={Percent}
            color="#06B6D4"
            deltaPercent={computeDelta(indicators.netMargin, prevIndicators?.netMargin)}
            delay={0.15}
          />
          <MetricCard
            label="期间费用率"
            value={formatPercent(indicators.expenseRatio)}
            icon={BarChart3}
            color="#F59E0B"
            deltaPercent={computeDelta(indicators.expenseRatio, prevIndicators?.expenseRatio)}
            delay={0.18}
          />
          <MetricCard
            label="ROE 净资产收益率"
            value={formatPercent(indicators.roe)}
            icon={TrendingUp}
            color="#EC4899"
            deltaPercent={computeDelta(indicators.roe, prevIndicators?.roe)}
            delay={0.21}
          />
          <MetricCard
            label="总资产"
            value={formatCurrency(indicators.totalAssets)}
            icon={PieChart}
            color="#06B6D4"
            deltaPercent={computeDelta(indicators.totalAssets, prevIndicators?.totalAssets)}
            delay={0.24}
          />
          <MetricCard
            label="总负债"
            value={formatCurrency(indicators.totalLiabilities)}
            icon={Wallet}
            color="#F59E0B"
            deltaPercent={computeDelta(indicators.totalLiabilities, prevIndicators?.totalLiabilities)}
            delay={0.27}
          />
          <MetricCard
            label="资产负债率"
            value={formatPercent(indicators.debtRatio)}
            icon={Scale}
            color="#EF4444"
            deltaPercent={computeDelta(indicators.debtRatio, prevIndicators?.debtRatio)}
            delay={0.3}
          />
          <MetricCard
            label="流动比率"
            value={indicators.currentRatio.toFixed(2)}
            icon={Activity}
            color="#10B981"
            deltaPercent={computeDelta(indicators.currentRatio, prevIndicators?.currentRatio)}
            delay={0.33}
          />
          <MetricCard
            label="权益乘数"
            value={indicators.equityMultiplier.toFixed(2)}
            icon={BarChart3}
            color="#8B5CF6"
            deltaPercent={computeDelta(indicators.equityMultiplier, prevIndicators?.equityMultiplier)}
            delay={0.36}
          />
          <MetricCard
            label="现金净增加额"
            value={formatCurrency(indicators.cashNetIncrease)}
            icon={Wallet}
            color="#10B981"
            deltaPercent={computeDelta(indicators.cashNetIncrease, prevIndicators?.cashNetIncrease)}
            delay={0.39}
          />
          <MetricCard
            label="营业收入"
            value={formatCurrency(indicators.revenue)}
            icon={TrendingUp}
            color="#10B981"
            deltaPercent={computeDelta(indicators.revenue, prevIndicators?.revenue)}
            delay={0.42}
          />
          <MetricCard
            label="平衡校验"
            value={formatCurrency(indicators.balanceCheck)}
            icon={Scale}
            color={Math.abs(indicators.balanceCheck) < 0.01 ? "#10B981" : "#EF4444"}
            delay={0.45}
          />
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="flex items-center gap-1 border-b border-white/[0.06]">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative flex items-center gap-1.5 px-3 py-2 text-[11px] font-mono transition-colors ${
                active ? "text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              <Icon size={12} />
              {tab.label}
              {active && (
                <motion.div
                  layoutId="statement-tab-underline"
                  className="absolute left-0 right-0 bottom-0 h-0.5 bg-cyan-400"
                  style={{ boxShadow: "0 0 8px rgba(34,211,238,0.6)" }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* 报表内容（可钻取） */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md overflow-hidden">
        {/* 表头 */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06] bg-white/[0.015]">
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            {tabs.find((t) => t.key === activeTab)?.label}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-mono text-gray-600">点击科目可展开明细</span>
          </div>
        </div>
        {/* 行列表 */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            {activeRows.map((row, idx) => (
              <StatementRow key={row.key} row={row} delay={idx * 0.03} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* 数据来源图例 */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-[9px] font-mono text-gray-600">数据来源：</span>
        <DataSourceBadge source="auto" />
        <DataSourceBadge source="manual" />
        <DataSourceBadge source="empty" />
      </div>

      {/* 录入表单 */}
      <AnimatePresence>
        {showEntryForm && (
          <EntryFormModal
            current={data}
            revenueSource={revenueSource}
            onClose={() => setShowEntryForm(false)}
            onSave={handleSaveEntry}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：人工录入表单（Modal）
// ============================================

interface EntryFormModalProps {
  current: StatementData;
  revenueSource: "real" | "partial" | "degraded";
  onClose: () => void;
  onSave: (formData: Partial<StatementData>) => void;
}

function EntryFormModal({ current, revenueSource, onClose, onSave }: EntryFormModalProps) {
  // 表单状态（仅手动字段，收入只读）
  const [form, setForm] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of MANUAL_FIELDS) {
      init[f.key] = String((current[f.key] as number) ?? 0);
    }
    return init;
  });

  const handleFieldChange = (key: string, value: string) => {
    // 仅允许数字与小数点
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleSubmit = () => {
    const formData: Partial<StatementData> = {};
    for (const f of MANUAL_FIELDS) {
      const num = parseFloat(form[f.key]);
      (formData as Record<string, number | string>)[f.key] = isNaN(num) ? 0 : num;
    }
    onSave(formData);
  };

  // 按报表分组
  const groups = ["利润表", "资产负债表", "现金流量表"] as const;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-xl border border-white/[0.08] bg-[#0a0e17]/95 backdrop-blur-xl flex flex-col"
      >
        {/* 顶部光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Edit3 size={14} className="text-cyan-400" />
            <span className="text-[12px] font-mono text-white">财务数据录入</span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 收入只读提示（自动同步） */}
        <div className="px-4 py-2 border-b border-white/[0.04] bg-emerald-500/[0.03]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database size={11} className="text-emerald-400" />
              <span className="text-[10px] font-mono text-gray-300">营业收入（自动同步）</span>
              <DataSourceBadge source="auto" />
            </div>
            <span className="text-[12px] font-mono text-emerald-300">
              {formatCurrency(current.revenue)}
            </span>
          </div>
          <p className="text-[9px] font-mono text-gray-600 mt-1">
            收入由 RevenueEngine 自动聚合，不可手动修改。当前状态：
            {revenueSource === "real"
              ? "已接入真实数据"
              : revenueSource === "partial"
              ? "部分真实数据"
              : "未接入（待同步）"}
          </p>
        </div>

        {/* 表单内容（按分组） */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {groups.map((group) => (
            <div key={group}>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  {group}
                </span>
                <div className="flex-1 h-px bg-white/[0.04]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {MANUAL_FIELDS.filter((f) => f.group === group).map((f) => (
                  <div
                    key={f.key}
                    className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] font-mono text-gray-300">{f.label}</label>
                      <DataSourceBadge source="manual" />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] font-mono text-gray-600">¥</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form[f.key]}
                        onChange={(e) => handleFieldChange(f.key, e.target.value)}
                        placeholder="0.00"
                        className="flex-1 bg-transparent text-[12px] font-mono text-white outline-none placeholder:text-gray-700"
                      />
                    </div>
                    <p className="text-[8px] font-mono text-gray-600 mt-1">{f.hint}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06] bg-white/[0.015]">
          <p className="text-[9px] font-mono text-gray-600">
            保存后将生成新快照，原数据转为"上月"用于同比/环比对比
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={onClose}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-mono rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition"
            >
              <X size={10} />
              取消
            </button>
            <button
              onClick={handleSubmit}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-mono rounded border border-emerald-500/30 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition"
            >
              <Save size={10} />
              保存
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
